import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import {
  getSubstitutionHistory,
  type GetSubstitutionHistoryParams,
  type SubstitutionHistory,
  type SubstitutionHistoryResponse,
} from '@/lib/network';

export const SUBSTITUTION_HISTORY_QUERY_KEY_PREFIX = ['substitutions', 'history'] as const;
const SUBSTITUTION_HISTORY_STALE_TIME_MS = 30 * 1000;
const SUBSTITUTION_HISTORY_GC_TIME_MS = 5 * 60 * 1000;

export const getSubstitutionHistoryQueryKey = (teamId: string | undefined) =>
  [...SUBSTITUTION_HISTORY_QUERY_KEY_PREFIX, teamId ?? ''] as const;

export type UseSubstitutionHistoryOptions = Omit<
  UseQueryOptions<SubstitutionHistoryResponse, Error, SubstitutionHistory>,
  'queryKey' | 'queryFn' | 'select'
>;

export const useSubstitutionHistory = (
  params: Partial<GetSubstitutionHistoryParams>,
  options?: UseSubstitutionHistoryOptions
): UseQueryResult<SubstitutionHistory, Error> => {
  const { enabled = true, ...restOptions } = options ?? {};
  const teamId = params.teamId;

  return useQuery<SubstitutionHistoryResponse, Error, SubstitutionHistory>({
    queryKey: getSubstitutionHistoryQueryKey(teamId),
    queryFn: async () => {
      if (!teamId) {
        throw new Error('teamId is required to fetch substitution history');
      }
      return getSubstitutionHistory({ teamId });
    },
    select: response => response.data,
    enabled: enabled && !!teamId,
    staleTime: SUBSTITUTION_HISTORY_STALE_TIME_MS,
    gcTime: SUBSTITUTION_HISTORY_GC_TIME_MS,
    ...restOptions,
  });
};
